import { ValidationError } from '~/errors';
import { validateId } from '~/repositories/utils';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import type {
  CreateRecurringLeaseChargeDto,
  GetLeaseRecurringChargeParams,
  RecurringLeaseChargeDto,
  RecurringLeaseChargesDto,
  UpdateRecurringLeaseChargeDto,
} from '~/swagger/portal/Api';

const LEASES_URL = '/api/leases';

export type GetLeaseRecurringChargeParamsInput = Omit<GetLeaseRecurringChargeParams, 'leaseId'> & {
  leaseId: string | number;
};

export class LeaseRecurringChargeRepository extends BaseRestRepository {
  public async getRecurringCharges(params: GetLeaseRecurringChargeParamsInput): Promise<RecurringLeaseChargesDto> {
    const { leaseId, ...queryParams } = params;
    const baseUrl = `${LEASES_URL}/${String(leaseId)}/charges/recurring-charges`;
    const url = urlWithQuery(baseUrl, queryParams);
    const response = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    return this.getJsonResponse(response);
  }

  public async addRecurringCharge(leaseId: string, payload: CreateRecurringLeaseChargeDto): Promise<RecurringLeaseChargeDto> {
    const url = `${LEASES_URL}/${leaseId}/charges/recurring-charges`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async updateRecurringCharge(leaseId: string, recurringId: string, payload: UpdateRecurringLeaseChargeDto): Promise<void> {
    validateId(recurringId);
    if (!recurringId) throw new ValidationError('Recurring ID must be provided');
    const url = `${LEASES_URL}/${leaseId}/charges/recurring-charges/${recurringId}`;
    await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  public async deleteRecurringCharge(leaseId: string, recurringId: string, deletePostedCharges: boolean): Promise<void> {
    validateId(recurringId);
    if (!recurringId) throw new ValidationError('Recurring ID must be provided');
    const url = `${LEASES_URL}/${leaseId}/charges/recurring-charges/${recurringId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'DELETE',
      body: JSON.stringify({ deletePostedCharges }),
    });
    if (!response.ok) {
      console.error(await response.json());
    }
  }
}
